const METADATA_TIMEOUT_MS = 15_000;

type VideoMetadataType = {
    durationSec: number;
    width: number;
    height: number;
};

/**
 * Returns video metadata (duration in seconds and frame size) from an object URL
 * (e.g. from URL.createObjectURL(file)).
 * Caller is responsible for creating and revoking the URL.
 * Rejects if the video metadata cannot be loaded.
 */
export function getVideoMetadataFromObjectUrl(objectUrl: string): Promise<VideoMetadataType> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');

        video.preload = 'metadata';

        // The timer id has to be reachable from cleanup(), which the timeout callback itself calls,
        // so it lives in a holder the cleanup can close over before the timer exists.
        const timeout: {id?: ReturnType<typeof setTimeout>} = {};

        function cleanup() {
            clearTimeout(timeout.id);
            video.remove();
            video.src = '';
        }

        // A container the browser cannot parse leaves the element in readyState 0 forever without
        // firing either event, so without this the caller never settles and the upload spinner
        // stays on screen until the page is reloaded.
        timeout.id = setTimeout(() => {
            cleanup();
            reject(new Error('Timed out reading video metadata'));
        }, METADATA_TIMEOUT_MS);

        video.addEventListener('loadedmetadata', () => {
            const {duration, videoWidth, videoHeight} = video;

            cleanup();

            if (Number.isFinite(duration) && duration >= 0) {
                resolve({durationSec: duration, width: videoWidth, height: videoHeight});
            } else {
                reject(new Error('Invalid video duration'));
            }
        });

        video.addEventListener('error', () => {
            cleanup();
            reject(new Error('Failed to load video'));
        });

        video.src = objectUrl;
    });
}
